import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import type { AuthSession, CompanySignup } from "../../types/vtc";
import { AUTH_CONTENT, COMPANY_BUSINESS_TYPES, OWNER_TYPES } from "../../data/auth";
import { useT } from "../../i18n";
import { registerCompany, saveSession } from "../../lib/auth";
import { toast } from "../../lib/toast";
import { cn } from "../../lib/format";
import Button from "../ui/Button";
import { FormAlert, PasswordField, SelectField, TextField } from "../ui/FormField";
import EmailCodeForm from "./EmailCodeForm";
import Turnstile from "../ui/Turnstile";
import { turnstileEnabled } from "../../lib/turnstile";
import { emailRules, phoneRules, required } from "./rules";

/** confirmPassword is only checked here; it is never sent to the backend. */
type CompanySignupValues = CompanySignup & { confirmPassword: string };

/**
 * "Create a rental account": one page for individuals and companies.
 * After "Create account" the backend e-mails a 6-digit code; entering it logs
 * the account in. SIRET, address and verification documents are added later, from the account.
 */
export default function CompanySignupForm() {
  const t = useT();
  const f = t.auth.companySignup;
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Cloudflare Turnstile (bot check); one token per attempt
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  /** Set once the account is created: the code was sent to this address. */
  const [codeEmail, setCodeEmail] = useState("");

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CompanySignupValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      ownerType: "",
      companyName: "",
      businessType: "",
    },
  });
  const [ownerType, businessType] = useWatch({ control, name: ["ownerType", "businessType"] });
  const isCompany = ownerType === "company";

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    if (turnstileEnabled && !captcha) return setSubmitError(t.captcha.required);
    try {
      // Drop confirmPassword: the match is a front-end check only.
      const { firstName, lastName, email, phone, password, ownerType, companyName, businessType } =
        values;
      const { email: sentTo } = await registerCompany({
        firstName,
        lastName,
        email,
        phone,
        password,
        ownerType,
        companyName,
        businessType,
      }, captcha);
      setCodeEmail(sentTo);
      toast.success(t.auth.driverSignup.codeSentTo(sentTo));
      window.scrollTo({ top: 0 });
    } catch (err) {
      const message = err instanceof Error ? err.message : t.common.somethingWrong;
      setSubmitError(message);
      toast.error(message);
      setCaptchaReset((n) => n + 1); // the token was used: get a new one
    }
  });

  const onVerified = (session: AuthSession, message: string) => {
    saveSession(session, true);
    toast.success(message);
    navigate(AUTH_CONTENT.company.dashboardHref);
  };

  if (codeEmail) return <EmailCodeForm role="company" email={codeEmail} onVerified={onVerified} />;

  /** Required only when "Company" is picked. */
  const companyOnly = (message: string) => ({
    validate: (v: string, values: CompanySignupValues) =>
      values.ownerType !== "company" || v.trim() !== "" || message,
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <TextField
          label={t.auth.driverSignup.firstName}
          placeholder={t.common.ph.firstName}
          autoComplete="given-name"
          error={errors.firstName?.message}
          {...register("firstName", required(t.auth.driverSignup.firstNameRequired))}
        />
        <TextField
          label={t.auth.driverSignup.lastName}
          placeholder={t.common.ph.lastName}
          autoComplete="family-name"
          error={errors.lastName?.message}
          {...register("lastName", required(t.auth.driverSignup.lastNameRequired))}
        />
        <TextField
          label={f.emailAddress}
          type="email"
          placeholder={t.common.emailPlaceholder}
          autoComplete="email"
          error={errors.email?.message}
          {...register("email", emailRules(t))}
        />
        <TextField
          label={f.phoneNumber}
          type="tel"
          autoComplete="tel"
          placeholder="06 12 34 56 78"
          error={errors.phone?.message}
          {...register("phone", phoneRules(t))}
        />
        <PasswordField
          label={t.common.password}
          autoComplete="new-password"
          placeholder={f.passwordPlaceholder}
          error={errors.password?.message}
          {...register("password", {
            required: t.auth.rules.passwordRequired,
            minLength: { value: 8, message: t.auth.rules.passwordMin },
          })}
        />
        <PasswordField
          label={t.auth.driverSignup.confirmPassword}
          autoComplete="new-password"
          placeholder={f.passwordPlaceholder}
          error={errors.confirmPassword?.message}
          {...register("confirmPassword", {
            required: t.auth.driverSignup.confirmRequired,
            validate: (v, values) => v === values.password || t.auth.driverSignup.mismatch,
          })}
        />
      </div>

      <fieldset className="m-0 mt-5.5 border-0 p-0">
        <legend className="mb-2 p-0 text-[13px] font-medium text-ink-soft">{f.ownerType}</legend>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {OWNER_TYPES.map((type) => (
            <label
              key={type}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-card border bg-white px-4 py-3.5 transition-colors",
                "has-checked:border-brand has-checked:bg-[#F5F8FE] has-focus-visible:ring-2 has-focus-visible:ring-brand/30",
                errors.ownerType ? "border-tone-red-fg" : "border-line-strong",
              )}
            >
              <input
                type="radio"
                value={type}
                className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
                {...register("ownerType", { required: f.ownerTypeRequired })}
              />
              <span className="flex flex-col gap-1">
                <span className="text-[15px] font-medium text-ink">{f.ownerTypes[type].label}</span>
                <span className="text-[13px] leading-[1.45] text-muted">{f.ownerTypes[type].desc}</span>
              </span>
            </label>
          ))}
        </div>
        {errors.ownerType && (
          <p className="mt-1.5 mb-0 text-xs text-tone-red-fg">{errors.ownerType.message}</p>
        )}
        {ownerType === "individual" && (
          <p className="mt-2 mb-0 text-[13px] leading-normal text-muted">{f.individualNote}</p>
        )}
      </fieldset>

      {isCompany && (
        <div className="mt-4 grid gap-3.5 sm:grid-cols-2">
          <TextField
            label={f.companyName}
            autoComplete="organization"
            placeholder={f.companyNamePlaceholder}
            error={errors.companyName?.message}
            {...register("companyName", companyOnly(f.companyNameRequired))}
          />
          <SelectField
            label={f.businessType}
            options={["", ...COMPANY_BUSINESS_TYPES]}
            labels={{ "": f.selectPlaceholder, ...f.businessTypes }}
            error={errors.businessType?.message}
            {...register("businessType", companyOnly(f.businessTypeRequired))}
          />
          {businessType && (
            <p className="m-0 text-[13px] leading-[1.45] text-muted sm:col-span-2">
              {f.businessTypeDescs[businessType]}
            </p>
          )}
        </div>
      )}

      <Turnstile onToken={setCaptcha} resetKey={captchaReset} className="mt-5" />

      {submitError && (
        <div className="mt-4">
          <FormAlert>{submitError}</FormAlert>
        </div>
      )}

      <Button
        type="submit"
        disabled={isSubmitting}
        className="mt-7 h-11 w-full cursor-pointer border-0"
      >
        {isSubmitting ? f.creating : f.createAccount}
      </Button>
    </form>
  );
}
